import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { ReuseModal } from "../ReuseModal";
import { CustomButton } from "../CustomButton";
import { MingcutePdfIcon, TriangleIcon } from "../../images";
import { CustomCheckbox } from "../CustomCheckbox";
import { AccountPlan, SubTopic, Topic } from "../../../types";
import { useGetPDFPlanMutation } from "../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";

const getFilteredPlan = (
  plan: AccountPlan,
  selectedArticles: Record<string, boolean>,
  option: string,
) => {
  if (option === "All Sections") {
    return plan;
  }

  return {
    topics: plan.topics
      .map((topic) => {
        if (!topic.subTopics || topic.subTopics.length === 0) {
          if (selectedArticles[topic.id]) {
            return topic;
          }
          return null;
        }

        const filteredSubTopics = topic.subTopics
          .map((sub) => {
            if (selectedArticles[sub.id]) return sub;

            if (
              sub.article.length === 0 ||
              sub.article.every(({ title }) => !title)
            ) {
              return null;
            }

            const filteredArticles = sub.article.filter(
              (a) => !a.title || selectedArticles[a.id],
            );
            if (
              filteredArticles.length === 0 ||
              filteredArticles.every(({ title }) => !title)
            )
              return null;

            return {
              ...sub,
              article: filteredArticles,
            };
          })
          .filter(Boolean);

        if (
          filteredSubTopics.length === 0 ||
          filteredSubTopics.every((subTopic) => !subTopic?.title)
        )
          return null;

        return {
          ...topic,
          subTopics: filteredSubTopics,
        };
      })
      .filter(Boolean),
  };
};

export const ExportAccountPlanBtn = ({
  isFixed,
  account_id,
  campaign_id,
  accountPlan,
  companyName,
}: {
  isFixed: boolean;
  account_id: string;
  campaign_id: string;
  accountPlan?: AccountPlan;
  companyName: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState<
    Record<string, boolean>
  >({});
  const [openTopicId, setOpenTopicId] = useState<string | null>(null);
  const [openSubTopics, setOpenSubTopics] = useState<Record<string, boolean>>(
    {},
  );
  const [option, setOption] = useState<string>("All Sections");

  const [getPDFPlan, { isLoading }] = useGetPDFPlanMutation();

  useEffect(() => {
    if (accountPlan) {
      setOpenTopicId(null);
      const initialSelected: Record<string, boolean> = {};
      accountPlan.topics.forEach((topic) =>
        topic.subTopics.forEach((sub) => {
          if (
            sub.article.length === 0 ||
            sub.article.every(({ title }) => !title)
          ) {
            initialSelected[sub.id] = true;
          } else {
            sub.article.forEach((article) => {
              initialSelected[article.id] = true;
            });
          }
        }),
      );
      accountPlan.topics.forEach((topic) => {
        if (topic.subTopics.length === 0) {
          initialSelected[topic.id] = true;
        }
      });
      setSelectedArticles(initialSelected);
    }
  }, [isOpen, accountPlan]);

  const openToggleTopic = (topicId: string) => {
    setOpenTopicId((prev) => (prev === topicId ? null : topicId));
    setOpenSubTopics({});
  };

  const openToggleSubTopic = (subId: string) => {
    setOpenSubTopics((prev) => ({
      ...prev,
      [subId]: !prev[subId],
    }));
  };

  const selectToggleArticle = (title: string) => {
    setSelectedArticles((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const isSubTopicFullySelected = (subTopic: SubTopic): boolean => {
    if (
      subTopic.article.length === 0 ||
      !subTopic.article.every(({ title }) => title)
    )
      return selectedArticles[subTopic.id];
    return subTopic.article.every((a) => !a.title || selectedArticles[a.id]);
  };

  const isTopicFullySelected = (topic: Topic): boolean => {
    if (topic.subTopics.length === 0) return selectedArticles[topic.id];
    return topic.subTopics.every((sub) => isSubTopicFullySelected(sub));
  };

  const isSubTopicIndeterminate = (subTopic: SubTopic): boolean => {
    if (subTopic.article.length === 0) return false;
    const selectedCount = subTopic.article.filter(
      (a) => selectedArticles[a.id],
    ).length;
    return selectedCount > 0 && selectedCount < subTopic.article.length;
  };

  const isTopicIndeterminate = (topic: Topic): boolean => {
    const selectedIndeterminateCount = topic.subTopics.filter((sub) =>
      isSubTopicIndeterminate(sub),
    ).length;

    const selectedFullyCount = topic.subTopics.filter((sub) =>
      isSubTopicFullySelected(sub),
    ).length;

    return (
      selectedIndeterminateCount + selectedFullyCount > 0 &&
      selectedFullyCount < topic.subTopics.length
    );
  };

  const setArticlesForSubtopic = (subTopic: SubTopic, checked: boolean) => {
    setSelectedArticles((prev) => {
      const newState = { ...prev };
      if (
        subTopic.article.length === 0 ||
        subTopic.article.every(({ title }) => !title)
      ) {
        if (checked) newState[subTopic.id] = true;
        else delete newState[subTopic.id];
      } else {
        subTopic.article.forEach((a) => {
          if (checked) newState[a.id] = true;
          else delete newState[a.id];
        });
      }
      return newState;
    });
  };

  const setArticlesForTopic = (topic: Topic, checked: boolean) => {
    setSelectedArticles((prev) => {
      const newState = { ...prev };
      if (topic.subTopics.length === 0) {
        if (checked) newState[topic.id] = true;
        else delete newState[topic.id];
      } else {
        topic.subTopics.forEach((sub) => {
          if (
            sub.article.length === 0 ||
            sub.article.every(({ title }) => !title)
          ) {
            if (checked) newState[sub.id] = true;
            else delete newState[sub.id];
          } else {
            sub.article.forEach((a) => {
              if (checked) newState[a.id] = true;
              else delete newState[a.id];
            });
          }
        });
      }
      return newState;
    });
  };

  const submitHandler = () => {
    if (!accountPlan) return;
    const topics = getFilteredPlan(accountPlan, selectedArticles, option);
    setIsOpen(false);

    getPDFPlan({ account_id, campaign_id, topics: topics.topics as Topic[] })
      .unwrap()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Account Plan for ${companyName}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Export account plan failed"}
          />,
        );
      });
  };

  return (
    <>
      <CustomButton
        onClick={() => setIsOpen(true)}
        variant="outlined"
        textColor="white"
        fullWidth={true}
        type="button"
        startIcon={
          isLoading ? (
            <CircularProgress size={20} sx={{ color: "inherit" }} />
          ) : (
            <MingcutePdfIcon color="white" />
          )
        }
        disabled={isLoading}
        sx={{
          borderColor: "white",
          backgroundColor: "transparent",
          "&.Mui-disabled": {
            color: "white",
            borderColor: "white",
          },
          "&.Mui-disabled svg": {
            color: "white",
          },
          "&:hover": {
            backgroundColor: "transparent",
            borderColor: "white",
          },
        }}
      >
        Export PDF
      </CustomButton>

      {isOpen && (
        <ReuseModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Export Plan"
          maxWidth="540px"
          submitBtnText="Export"
          submitBtnColor="#1C3C6C"
          onSubmit={submitHandler}
          cancelBtnText="Back"
          isLoading={isLoading}
          isDisabled={isLoading || Object.values(selectedArticles).length === 0}
          isDisabledCancelBtn={isLoading}
        >
          <Stack>
            <Stack gap="8px" paddingBottom="8px">
              <Typography
                component="span"
                variant="subtitle1"
                color="custom.textDarkBlue"
                fontWeight={500}
              >
                Sections
              </Typography>
              <FormControl sx={{ padding: "0 12px", alignItems: "flex-start" }}>
                <RadioGroup
                  sx={{ flexDirection: "row" }}
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue={option}
                  value={option}
                  name="radio-buttons-group"
                  onChange={(e, el) => setOption(el)}
                >
                  <FormControlLabel
                    value={"All Sections"}
                    control={<Radio />}
                    label={"All Sections"}
                  />
                  <FormControlLabel
                    value={"Selected Sections"}
                    control={<Radio />}
                    label={"Selected Sections"}
                  />
                </RadioGroup>
              </FormControl>
              {option === "Selected Sections" && (
                <Typography
                  component="span"
                  variant="body1"
                  color="text.secondary"
                >
                  Please choose sections you want to export.
                </Typography>
              )}
            </Stack>

            {option === "Selected Sections" &&
              accountPlan?.topics.map((topic) => (
                <NavTopic
                  key={topic.id}
                  topic={topic}
                  isOpen={openTopicId === topic.id}
                  onToggle={() => openToggleTopic(topic.id)}
                  openSubTopics={openSubTopics}
                  toggleSubTopic={openToggleSubTopic}
                  selectedArticles={selectedArticles}
                  toggleArticle={selectToggleArticle}
                  setArticlesForSubtopic={setArticlesForSubtopic}
                  setArticlesForTopic={setArticlesForTopic}
                  isTopicFullySelected={isTopicFullySelected}
                  isTopicIndeterminate={isTopicIndeterminate}
                  isSubTopicFullySelected={isSubTopicFullySelected}
                  isSubTopicIndeterminate={isSubTopicIndeterminate}
                />
              ))}
          </Stack>
        </ReuseModal>
      )}
    </>
  );
};

const NavTopic = ({
  topic,
  isOpen,
  onToggle,
  openSubTopics,
  toggleSubTopic,
  selectedArticles,
  toggleArticle,
  setArticlesForSubtopic,
  setArticlesForTopic,
  isTopicFullySelected,
  isTopicIndeterminate,
  isSubTopicFullySelected,
  isSubTopicIndeterminate,
}: {
  topic: Topic;
  isOpen: boolean;
  onToggle: () => void;
  openSubTopics: Record<string, boolean>;
  toggleSubTopic: (id: string) => void;
  selectedArticles: Record<string, boolean>;
  toggleArticle: (title: string) => void;
  setArticlesForSubtopic: (subTopic: SubTopic, checked: boolean) => void;
  setArticlesForTopic: (topic: Topic, checked: boolean) => void;
  isTopicFullySelected: (topic: Topic) => boolean;
  isTopicIndeterminate: (topic: Topic) => boolean;
  isSubTopicFullySelected: (subTopic: SubTopic) => boolean;
  isSubTopicIndeterminate: (subTopic: SubTopic) => boolean;
}) => {
  return (
    <Box>
      <ListItemButton
        onClick={() => {
          if (topic.subTopics.length === 0) {
            setArticlesForTopic(topic, !isTopicFullySelected(topic));
          }
          onToggle();
        }}
        sx={{ paddingLeft: "16px" }}
      >
        <ListItemIcon sx={{ minWidth: "10px" }}>
          {topic.subTopics.length > 0 &&
            (isOpen ? <TriangleIcon /> : <TriangleIcon rotate="-90" />)}
        </ListItemIcon>
        <CustomCheckbox
          onClick={(e) => {
            e.stopPropagation();
            setArticlesForTopic(topic, !isTopicFullySelected(topic));
          }}
          checked={isTopicFullySelected(topic)}
          indeterminate={isTopicIndeterminate(topic)}
        />
        <Typography>{topic.title}</Typography>
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {topic.subTopics.map((sub) => (
          <NavSubTopic
            key={sub.id}
            subTopic={sub}
            isOpen={openSubTopics[sub.id]}
            onToggle={() => toggleSubTopic(sub.id)}
            selectedArticles={selectedArticles}
            toggleArticle={toggleArticle}
            setArticlesForSubtopic={setArticlesForSubtopic}
            isSubTopicFullySelected={isSubTopicFullySelected}
            isSubTopicIndeterminate={isSubTopicIndeterminate}
          />
        ))}
      </Collapse>
    </Box>
  );
};

const NavSubTopic = ({
  subTopic,
  isOpen,
  onToggle,
  selectedArticles,
  toggleArticle,
  setArticlesForSubtopic,
  isSubTopicFullySelected,
  isSubTopicIndeterminate,
}: {
  subTopic: SubTopic;
  isOpen: boolean;
  onToggle: () => void;
  selectedArticles: Record<string, boolean>;
  toggleArticle: (title: string) => void;
  setArticlesForSubtopic: (subTopic: SubTopic, checked: boolean) => void;
  isSubTopicFullySelected: (subTopic: SubTopic) => boolean;
  isSubTopicIndeterminate: (subTopic: SubTopic) => boolean;
}) => {
  return (
    <Box>
      <ListItemButton
        onClick={() => {
          if (
            subTopic.article.length === 0 ||
            subTopic.article.every(({ title }) => !title)
          ) {
            setArticlesForSubtopic(
              subTopic,
              !isSubTopicFullySelected(subTopic),
            );
          }
          onToggle();
        }}
        sx={{ paddingLeft: "44px" }}
      >
        <ListItemIcon sx={{ minWidth: "10px" }}>
          {subTopic.article.length > 0 &&
            subTopic.article.some(({ title }) => title) &&
            (isOpen ? <TriangleIcon /> : <TriangleIcon rotate="-90" />)}
        </ListItemIcon>
        <CustomCheckbox
          onClick={(e) => {
            e.stopPropagation();
            setArticlesForSubtopic(
              subTopic,
              !isSubTopicFullySelected(subTopic),
            );
          }}
          checked={isSubTopicFullySelected(subTopic)}
          indeterminate={isSubTopicIndeterminate(subTopic)}
        />
        <Typography>{subTopic.title}</Typography>
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List dense disablePadding>
          {subTopic.article.map((article) => {
            if (!article.title) return null;

            return (
              <ListItemButton
                key={article.id}
                sx={{ pl: "54px" }}
                onClick={() => toggleArticle(article.id)}
              >
                <CustomCheckbox checked={selectedArticles[article.id]} />
                <Typography>{article.title}</Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
};
